import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  authorFeedUrl,
  followAuthor,
  getAuthorArticles,
  getAuthorFollowStatus,
  getAuthorProfile,
  unfollowAuthor,
} from '../services/authorApi';
import { normalizeMarkdownContent } from '../utils/markdownContent';
import { resolveMediaUrl } from '../utils/mediaUrl';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { formatLocalDate } from '../utils/formatDate';
import './AuthorChannelPage.css';
import './StringersPage.css';

function excerpt(text, maxLen = 160) {
  const plain = normalizeMarkdownContent(text || '')
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/!\[([^\]]*)\]\([^)]+\)/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/[#*_`>[\]]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  if (plain.length <= maxLen) return plain;
  return `${plain.slice(0, maxLen)}…`;
}

const AuthorChannelPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [author, setAuthor] = useState(null);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [following, setFollowing] = useState(false);
  const [followBusy, setFollowBusy] = useState(false);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    setError(null);
    Promise.all([getAuthorProfile(slug), getAuthorArticles(slug)])
      .then(([profile, page]) => {
        setAuthor(profile);
        setArticles(page.content || []);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (!slug || !isAuthenticated) {
      setFollowing(false);
      return;
    }
    getAuthorFollowStatus(slug)
      .then((data) => setFollowing(Boolean(data.following)))
      .catch(() => setFollowing(false));
  }, [slug, isAuthenticated]);

  useDocumentTitle(author?.displayName ? `${author.displayName} · Author channel` : 'Author channel');

  const handleFollowToggle = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    setFollowBusy(true);
    try {
      if (following) {
        await unfollowAuthor(slug);
        setFollowing(false);
      } else {
        await followAuthor(slug);
        setFollowing(true);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setFollowBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="author-channel-page">
        <div className="container">
          <p className="author-status">Loading channel…</p>
        </div>
      </div>
    );
  }

  if (error && !author) {
    return (
      <div className="author-channel-page">
        <div className="container">
          <h1>Author not found</h1>
          <p className="author-error">{error || 'No channel exists for this slug.'}</p>
          <Link to="/authors" className="btn btn-outline-primary">
            Browse authors
          </Link>
        </div>
      </div>
    );
  }

  const feedUrl = authorFeedUrl(slug);

  return (
    <div className="author-channel-page">
      <div className="container">
        <header className="author-header">
          <p className="author-kicker">Author channel</p>
          <h1>{author.displayName}</h1>
          {author.bio && <p className="author-bio">{author.bio}</p>}
          <div className="author-actions">
            <button
              type="button"
              className={`btn ${following ? 'btn-outline-secondary' : 'btn-primary'}`}
              onClick={handleFollowToggle}
              disabled={followBusy}
            >
              {following ? 'Following' : 'Follow channel'}
            </button>
            <a href={feedUrl} className="author-rss-link" title="Subscribe via RSS">
              RSS
            </a>
            <Link to="/authors" className="author-directory-link">
              All authors
            </Link>
            <Link to="/stringers" className="author-directory-link">
              Stringers
            </Link>
          </div>
        </header>

        {error && <p className="author-error">{error}</p>}

        <section className="author-posts">
          <h2>Published</h2>
          {articles.length === 0 ? (
            <p className="author-empty">No published articles yet.</p>
          ) : (
            <ul className="stringers-grid">
              {articles.map((article) => {
                const href = `/authors/${slug}/${article.id}`;
                const hasVideo = Boolean(article.mediaUrl);
                const poster = resolveMediaUrl(article.posterUrl);
                const mediaBlock = hasVideo ? (
                  <Link to={href} className="stringers-card-media" aria-label={`Play ${article.title}`}>
                    {poster ? (
                      <img src={poster} alt="" className="stringers-card-poster" />
                    ) : (
                      <div className="stringers-card-poster-fallback" />
                    )}
                    <span className="stringers-card-play">▶</span>
                    {article.articleType === 'newsagent_short' && (
                      <span className="stringers-card-badge">Short</span>
                    )}
                  </Link>
                ) : null;
                return (
                  <li
                    key={article.id}
                    className={`stringers-card${hasVideo ? ' stringers-card--video' : ''}`}
                  >
                    {mediaBlock}
                    <Link to={href} className="stringers-card-title">
                      {article.title}
                    </Link>
                    <p className="stringers-card-meta">
                      {article.publishedAt ? formatLocalDate(article.publishedAt) : '—'}
                      {hasVideo ? ' · Video' : ''}
                      {article.articleType === 'newsagent_short' ? ' · NewsAgent' : ''}
                    </p>
                    <p className="stringers-card-excerpt">
                      {excerpt(article.summary || article.content)}
                    </p>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
};

export default AuthorChannelPage;
